import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { CONTACT, SERVICES } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD = "w-full rounded-xl border border-white/12 bg-white/[0.03] px-4 py-3.5 font-body text-sm text-white placeholder:text-neutral-500 outline-none transition-colors duration-300 focus:border-[#ffb000]/70 focus:bg-white/[0.06]";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", vehicle: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Please add your name, phone and a short message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/enquiries`, form);
      setDone(true);
      toast.success(`Enquiry received — ref ${data.ref}. ${CONTACT.owner} will be in touch.`);
      setForm({ name: "", phone: "", email: "", vehicle: "", service: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact" className="relative overflow-hidden carbon px-5 py-28 md:px-10 md:py-40">
      <div className="absolute left-1/2 top-0 h-[1px] w-[70%] -translate-x-1/2 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-14 lg:grid-cols-2 lg:gap-20">
        {/* Left: comms */}
        <div>
          <span className="font-accent text-xs uppercase tracking-[0.4em] text-[#ffb000]">Direct Comms</span>
          <h2 className="mt-4 font-display text-4xl font-700 uppercase leading-[0.95] chrome-text md:text-6xl">
            Secure Your Slot
          </h2>
          <p className="mt-6 max-w-md font-body text-base leading-relaxed text-neutral-400">
            No call centres, no runaround. Get straight through to the workshop. Tell us what's happening and
            we'll tell you exactly how we'll fix it.
          </p>

          <div className="mt-12 space-y-4">
            <a href={CONTACT.phoneHref} data-testid="contact-phone" className="group flex items-center gap-5 rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition-colors duration-300 hover:border-[#ffb000]/50">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#ffb000] text-black">
                <Phone className="h-5 w-5" strokeWidth={2.4} />
              </div>
              <div>
                <div className="font-accent text-[11px] uppercase tracking-[0.25em] text-neutral-500">Call {CONTACT.owner} Now</div>
                <div className="font-display text-lg chrome-text-soft">{CONTACT.phone}</div>
              </div>
            </a>
            <a href={CONTACT.emailHref} data-testid="contact-email" className="group flex items-center gap-5 rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition-colors duration-300 hover:border-white/30">
              <div className="grid h-12 w-12 place-items-center rounded-xl border border-white/15 text-white">
                <Mail className="h-5 w-5" strokeWidth={2} />
              </div>
              <div>
                <div className="font-accent text-[11px] uppercase tracking-[0.25em] text-neutral-500">Email</div>
                <div className="font-body text-base text-white">{CONTACT.email}</div>
              </div>
            </a>
            <div className="flex items-center gap-5 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
              <div className="grid h-12 w-12 place-items-center rounded-xl border border-white/15 text-white">
                <MapPin className="h-5 w-5" strokeWidth={2} />
              </div>
              <div>
                <div className="font-accent text-[11px] uppercase tracking-[0.25em] text-neutral-500">Coverage</div>
                <div className="font-body text-base text-white">{CONTACT.area}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: form */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="chrome-inset rounded-3xl border border-white/12 bg-white/[0.04] p-7 backdrop-blur-2xl md:p-10"
        >
          {done ? (
            <div data-testid="contact-success" className="flex min-h-[420px] flex-col items-center justify-center text-center">
              <CheckCircle2 className="h-16 w-16 text-[#ffb000]" strokeWidth={1.4} />
              <h3 className="mt-6 font-accent text-2xl uppercase tracking-wide text-white">Enquiry Locked In</h3>
              <p className="mt-3 max-w-sm font-body text-sm text-neutral-400">
                Cheers — {CONTACT.owner} will be in touch shortly. Need it sorted faster? Give us a ring.
              </p>
              <button data-testid="contact-reset" onClick={() => setDone(false)} className="mt-8 rounded-full border border-white/20 px-6 py-3 font-accent text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-white/5">
                Send Another
              </button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <input data-testid="input-name" className={FIELD} placeholder="Your name *" value={form.name} onChange={set("name")} />
                <input data-testid="input-phone" className={FIELD} placeholder="Phone number *" value={form.phone} onChange={set("phone")} />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <input data-testid="input-email" className={FIELD} placeholder="Email (optional)" value={form.email} onChange={set("email")} />
                <input data-testid="input-vehicle" className={FIELD} placeholder="Vehicle (make / model)" value={form.vehicle} onChange={set("vehicle")} />
              </div>
              <select data-testid="input-service" className={`${FIELD} appearance-none`} value={form.service} onChange={set("service")}>
                <option value="" className="bg-black">Select a service…</option>
                {SERVICES.map((s) => (
                  <option key={s.id} value={s.title} className="bg-black">{s.title}</option>
                ))}
                <option value="Other" className="bg-black">Other / Not sure</option>
              </select>
              <textarea data-testid="input-message" rows={4} className={`${FIELD} resize-none`} placeholder="What's happening with the vehicle? *" value={form.message} onChange={set("message")} />
              <button
                data-testid="contact-submit"
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center gap-3 rounded-full bg-[#ffb000] px-8 py-4 font-accent text-sm font-600 uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-[#ff8c00] disabled:opacity-60"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {loading ? "Sending…" : "Get My Quote"}
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
